import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Ban, RefreshCw } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { isOrgActive, orgLifecycleCopy } from "../lib/orgLifecycle";

/**
 * Phase 2D.1C — what a member of a NON-ACTIVE organization sees.
 *
 * Rendered instead of every tenant module, so nothing here requests quotations,
 * customers, dashboard data or settings: the person cannot navigate into a
 * blocked module and no failing request is ever retried in a loop. The only
 * actions are the ones that are valid in every lifecycle state — re-check once,
 * switch to another organization, sign out.
 *
 * The backend refuses the same requests on its own; this screen is the
 * explanation, never the enforcement.
 */
export default function OrganizationBlocked() {
  const { t } = useTranslation();
  const { currentOrg, orgs, switchOrg, refreshOrgs, signOut } = useAuth();
  const [checking, setChecking] = useState(false);

  const copy = orgLifecycleCopy(currentOrg, {
    title: t("lifecycle.blocked_title"),
    message: t("lifecycle.blocked_body"),
  });
  const others = (orgs || []).filter((o) => o && o.id !== currentOrg?.id && isOrgActive(o));

  // One explicit re-check per click. There is no interval and no automatic
  // retry: a blocked organization stays blocked until a platform administrator
  // changes it.
  const recheck = async () => {
    if (checking) return;
    setChecking(true);
    try {
      await refreshOrgs();
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="qw-lifecycle-gate" data-testid="org-blocked-screen">
      <div className="qw-card qw-lifecycle-card">
        <div className="brand" style={{ marginBottom: 18 }}>OFFERTRACE</div>
        <div className="hstack" style={{ gap: 10, marginBottom: 10 }}>
          <Ban size={18} strokeWidth={2} aria-hidden="true" />
          <strong data-testid="org-blocked-title">{copy.title}</strong>
        </div>
        {currentOrg?.name && (
          <div className="qw-lifecycle-org" data-testid="org-blocked-org-name">
            {currentOrg.name}
          </div>
        )}
        <p className="qw-help" data-testid="org-blocked-message">{copy.message}</p>

        {others.length > 0 && (
          <div className="qw-lifecycle-others" data-testid="org-blocked-others">
            <div className="qw-help" style={{ marginBottom: 8 }}>
              {t("lifecycle.switch_hint")}
            </div>
            {others.map((o) => (
              <button
                key={o.id}
                type="button"
                className="qw-btn"
                onClick={() => switchOrg(o.id)}
                data-testid={`org-blocked-switch-${o.id}`}
              >
                {o.name}
              </button>
            ))}
          </div>
        )}

        <div className="qw-lifecycle-actions">
          <button
            type="button"
            className="qw-btn"
            onClick={recheck}
            disabled={checking}
            data-testid="org-blocked-recheck"
          >
            <RefreshCw size={14} strokeWidth={2} aria-hidden="true" />
            {checking ? t("lifecycle.rechecking") : t("lifecycle.recheck")}
          </button>
          <button
            type="button"
            className="qw-btn primary"
            onClick={signOut}
            data-testid="org-blocked-signout"
          >
            {t("auth.sign_out")}
          </button>
        </div>
      </div>
    </div>
  );
}
